import React, { useState } from 'react';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

/** Shown under a disabled "Send reminder" item when the patient can't be reached. */
const NO_CONTACT_REASON = 'No phone or email on file';

export interface AppointmentActionsMenuProps {
  /** Whether the gated "Send reminder" action is available. */
  canSendReminder: boolean;
  /**
   * Whether the patient has a phone or email a reminder could reach. When
   * false, the action is shown disabled with a reason rather than letting the
   * provider trigger a send that can't be delivered.
   */
  reminderReachable: boolean;
  /** A reminder send is in flight. */
  isSendingReminder: boolean;
  onSendReminder: () => void;
  /** Whether the "Delete appointment" action is available. */
  canDelete: boolean;
  /** A delete is in flight. */
  isDeleting: boolean;
  onDelete: () => void;
}

/**
 * Overflow (⋮) menu for the appointment detail drawer header.
 *
 * Renders nothing when no action is available. Owns its own open state and
 * closes before invoking a callback, so the parent only wires intent + busy
 * flags. Kept as a pure-prop component so its states are storybook-able
 * without mocking the whole drawer.
 */
export const AppointmentActionsMenu = ({
  canSendReminder,
  reminderReachable,
  isSendingReminder,
  onSendReminder,
  canDelete,
  isDeleting,
  onDelete,
}: AppointmentActionsMenuProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const isBusy = isDeleting || isSendingReminder;

  if (!canSendReminder && !canDelete) {
    return null;
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleSendReminderClick = () => {
    handleCloseMenu();
    onSendReminder();
  };

  const handleDeleteClick = () => {
    handleCloseMenu();
    onDelete();
  };

  return (
    <>
      <IconButton
        onClick={handleOpenMenu}
        size="small"
        aria-label="Appointment actions"
        aria-controls={isMenuOpen ? 'appointment-detail-actions-menu' : undefined}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        disabled={isBusy}
      >
        {isBusy ? <CircularProgress size={18} /> : <MoreVertIcon fontSize="small" />}
      </IconButton>
      <Menu
        id="appointment-detail-actions-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canSendReminder && (
          <MenuItem
            onClick={handleSendReminderClick}
            disabled={isSendingReminder || !reminderReachable}
          >
            <ListItemIcon>
              {isSendingReminder ? (
                <CircularProgress size={18} />
              ) : (
                <NotificationsActiveOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component="span" variant="body1">
                  Send reminder
                </Typography>
              }
              secondary={reminderReachable ? undefined : NO_CONTACT_REASON}
            />
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick} disabled={isDeleting}>
            <ListItemIcon>
              <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component="span" color="error" variant="body1">
                  Delete appointment
                </Typography>
              }
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
